import React, { useState, useEffect } from "react";
import { Formik, Form, Field } from "formik";
import { FaRegEyeSlash, FaEye } from "react-icons/fa";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { UpdatePasswordSchema } from "../../../components/Helpers";
import Hoc from "../../../components/HOC";
import {
  requestExpiryPassword,
  requestUpdatePassword,
  setResetExpiryPassword,
  setResetUpdatePassword,
} from "../../../Redux/LoginState/LoginActionCreator";
import { toast } from "react-toastify";
import LOCAL_KEYS, {
  parseLocalStorageJSON,
} from "../../../helpers/Localstorage";
import Swal from "sweetalert2";

function PasswordExpired() {
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] =
    useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [missingCriteria, setMissingCriteria] = useState([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const ResetPasswordState = useSelector(
    (state) => state.ResetExpiryPasswordReducer
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userDetails = parseLocalStorageJSON(LOCAL_KEYS.USER_DETAILS);
  const checkPasswordStrength = (password) => {
    const criteria = [
      { test: (pw) => /[a-z]/.test(pw), message: "Include a lowercase letter" },
      {
        test: (pw) => /[A-Z]/.test(pw),
        message: "Include an uppercase letter",
      },
      { test: (pw) => /\d/.test(pw), message: "Include a number" },
      {
        test: (pw) => /[^a-zA-Z\d\s:?,]/.test(pw),
        message: "Include a special character",
      }, // Exclude "?"
      { test: (pw) => pw.length >= 8, message: "At least 8 characters long" },
    ];

    const unmetCriteria = criteria
      .filter((criterion) => !criterion.test(password))
      .map((criterion) => criterion.message);

    setMissingCriteria(unmetCriteria);
    setPasswordStrength(criteria.length - unmetCriteria.length);
  };

  const renderStrengthBar = () => {
    if (missingCriteria.length === 0) return null;

    const colors = [
      "bg-red-500",
      "bg-yellow-500",
      "bg-primary",
      "bg-green-500",
    ];
    const width = `${(passwordStrength / 5) * 100}%`;

    return (
      <div className="relative h-2 w-full bg-gray-200 rounded mt-2">
        <div
          className={`absolute top-0 left-0 h-2 rounded transition-all ${
            colors[passwordStrength - 1] || "bg-red-500"
          }`}
          style={{ width }}
        ></div>
      </div>
    );
  };

  //   const resetPassword = (values) => {
  //     dispatch(requestExpiryPassword(values));
  //   };
  useEffect(() => {
    if (ResetPasswordState.response && !ResetPasswordState.loading) {
      localStorage.clear();
      toast.success(
        "Your password was sucessfully updated.Please login to continue"
      );
      window.location.reload();
    }
    if (ResetPasswordState.error && !ResetPasswordState.loading) {
      if(ResetPasswordState.error.message){Swal.fire({
        title: ResetPasswordState.error.message,
        icon: "warning",
        showCancelButton: false,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Ok",
        backdrop: `rgba(0, 0, 0, 0.5)`,
      }).then((result) => {
        if (result.isConfirmed) {
          // navigate('/login');
          dispatch(setResetExpiryPassword())
        }
      });} 
      else{
      toast.error("Unable to update your password. Please try again");
      dispatch(setResetExpiryPassword());
    }}
  }, [ResetPasswordState]);

  return (
    <div className="flex items-center justify-center h-[90vh]">
      <Formik
        initialValues={{
          newPassword: "",
          confirmPassword: "",
          oldPassword: "",
        }}
        validationSchema={UpdatePasswordSchema}
        onSubmit={(values, { resetForm }) => {
          if (values.newPassword === values.confirmPassword) {
            dispatch(requestExpiryPassword(values));
            // resetForm();
          } else {
            toast.error("Passwords do not match");
            dispatch(setResetExpiryPassword());
          }
        }}
      >
        {({ errors, touched, resetForm, values }) => (
          <Form className="bg-white p-8 rounded-lg shadow-none w-96 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <h1 className="text-xl text-gray-800 font-semibold mb-6 text-center">
              Password expired. Please update your password to continue
            </h1>

            <div className="mb-4 relative">
              <label className="text-black-700 mb-2 text-lg">
                Old Password
              </label>
              <Field
                name="oldPassword"
                type={passwordVisible ? "text" : "password"}
                className="w-full p-1 border mb-6 rounded focus:outline-none hover:ring-2 hover:ring-blue-400 "
              />
              {passwordVisible ? (
                <FaEye
                  onClick={() => setPasswordVisible(false)}
                  className="absolute right-3 top-[34px] cursor-pointer"
                />
              ) : (
                <FaRegEyeSlash
                  onClick={() => setPasswordVisible(true)}
                  className="absolute right-3 top-[34px] cursor-pointer"
                />
              )}
              {errors.oldPassword && touched.oldPassword && (
                <p className="text-red-400">{errors.newPassword}</p>
              )}
            </div>

            {/* New Password Field */}
            <div className="mb-4 relative">
              <label className="text-black-700 mb-2 text-lg">
                New Password
              </label>
              <Field
                name="newPassword"
                type={isPasswordVisible ? "text" : "password"}
                className="w-full p-1 border mb-6 rounded focus:outline-none hover:ring-2 hover:ring-blue-400"
                onKeyUp={(e) => checkPasswordStrength(e.target.value)}
              />
              {isPasswordVisible ? (
                <FaEye
                  onClick={() => setIsPasswordVisible(false)}
                  className="absolute right-3 top-[34px] cursor-pointer"
                />
              ) : (
                <FaRegEyeSlash
                  onClick={() => setIsPasswordVisible(true)}
                  className="absolute right-3 top-[34px] cursor-pointer"
                />
              )}
              {errors.newPassword && touched.newPassword && (
                <p className="text-red-400">{errors.newPassword}</p>
              )}
              {/* Password Strength Bar */}
              {values.newPassword && renderStrengthBar()}

              {/* Missing Criteria */}
              {values.newPassword && missingCriteria.length > 0 && (
                <ul className="mt-2 text-sm text-gray-600">
                  {missingCriteria.map((criteria, index) => (
                    <li key={index} className="text-red-500">
                      {criteria}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Confirm Password Field */}
            <div className="mb-4 relative">
              <label className="text-black-700 mb-2 text-lg">
                Confirm Password
              </label>
              <Field
                name="confirmPassword"
                type={isConfirmPasswordVisible ? "text" : "password"}
                className="w-full p-1 border mb-6 rounded text-sm focus:outline-none hover:ring-2 hover:ring-blue-400"
              />
              {isConfirmPasswordVisible ? (
                <FaEye
                  onClick={() => setIsConfirmPasswordVisible(false)}
                  className="absolute right-3 top-[34px] cursor-pointer"
                />
              ) : (
                <FaRegEyeSlash
                  onClick={() => setIsConfirmPasswordVisible(true)}
                  className="absolute right-3 top-[34px] cursor-pointer"
                />
              )}
              {errors.confirmPassword && touched.confirmPassword && (
                <p className="text-red-400">{errors.confirmPassword}</p>
              )}
            </div>

            <button
              type="submit"
              className="w-full text-white font-medium text-sm bg-primary hover:bg-gray-500 px-4 py-2 rounded"
            >
              {"Submit"}
            </button>
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default Hoc(PasswordExpired);
